import { parse, isValid } from "date-fns";
import {
  BaselineDefaultsRentControlFormDefaults,
  BaselineDefaultsRentControlFormRentControlType,
  BaselineDefaultsRentControlFormRentControlTypeLimit
} from "api";

const dateFormat = "M/d/yyyy";

/*
 * A blank number means the rent control type does not cap it. Only a batch created before the rent control types
 * existed has one, because both numbers are required on a saved allowed increases row.
 */
const notConfiguredLabel = "—";

const parseEffectiveDate = function (
  effectiveDate: string | null | undefined
): Date | undefined {
  if (!effectiveDate) {
    return undefined;
  }

  const parsed = parse(effectiveDate, dateFormat, new Date());

  return isValid(parsed) ? parsed : undefined;
};

/*
 * The allowed increases that apply today, which is the same rule the calculator applies at the offer effective date.
 * A row with no effective date applies at any date, so it is superseded by any dated row that has taken effect.
 */
const getLimitAsOfToday = function (
  rentControlType:
    | BaselineDefaultsRentControlFormRentControlType
    | null
    | undefined
): BaselineDefaultsRentControlFormRentControlTypeLimit | undefined {
  const today = new Date();

  const applicable = (rentControlType?.limits || []).filter((limit) => {
    const effectiveDate = parseEffectiveDate(limit.effectiveDate);

    return !effectiveDate || effectiveDate <= today;
  });

  if (applicable.length === 0) {
    return undefined;
  }

  return applicable.reduce((latest, limit) => {
    const latestDate = parseEffectiveDate(latest.effectiveDate);
    const limitDate = parseEffectiveDate(limit.effectiveDate);

    if (!limitDate) {
      return latest;
    }

    return !latestDate || limitDate > latestDate ? limit : latest;
  });
};

/*
 * In the order the allowed increases take effect, matching how the rent control types page lists them. A row with no
 * effective date applies at any date, so it sorts first.
 */
const getLimitsInEffectiveDateOrder = function (
  rentControlType:
    | BaselineDefaultsRentControlFormRentControlType
    | null
    | undefined
): BaselineDefaultsRentControlFormRentControlTypeLimit[] {
  return (rentControlType?.limits || []).slice().sort((first, second) => {
    const firstDate = parseEffectiveDate(first.effectiveDate);
    const secondDate = parseEffectiveDate(second.effectiveDate);

    if (!firstDate && !secondDate) {
      return 0;
    }

    if (!firstDate) {
      return -1;
    }

    if (!secondDate) {
      return 1;
    }

    return firstDate.getTime() - secondDate.getTime();
  });
};

/*
 * A batch created before the rent control types existed holds its maximum percentages and its effective date on the
 * rent control row itself and selects no rent control type. Those are what it was calculated with, so they are read
 * from the row in place of a rent control type's allowed increases. It never limited the number of increases.
 */
const getLimitFromRow = function (
  defaults: BaselineDefaultsRentControlFormDefaults
): BaselineDefaultsRentControlFormRentControlTypeLimit | undefined {
  if (
    defaults.maxPercentageByLaw == null &&
    defaults.maxPercentageByRevenueManager == null
  ) {
    return undefined;
  }

  return {
    effectiveDate: defaults.effectiveDate,
    maxPercentageByLaw: defaults.maxPercentageByLaw,
    maxPercentageByRevenueManager: defaults.maxPercentageByRevenueManager
  };
};

const getPercentageLabel = function (
  percentage: number | null | undefined
): string {
  return percentage == null ? notConfiguredLabel : `${percentage}%`;
};

const getNumberOfIncreasesLabel = function (
  numberOfIncreases: number | null | undefined
): string {
  return numberOfIncreases == null
    ? notConfiguredLabel
    : `${numberOfIncreases}`;
};

/**
 * The two allowed increases as one value, read as first 12 months then after 12 months.
 */
const getIncreasesAllowedLabel = function (
  limit: BaselineDefaultsRentControlFormRentControlTypeLimit | undefined
): string {
  if (!limit) {
    return notConfiguredLabel;
  }

  return `${getNumberOfIncreasesLabel(
    limit.increasesAllowedInFirstTwelveMonths
  )} / ${getNumberOfIncreasesLabel(limit.increasesAllowedAfterTwelveMonths)}`;
};

const rentControlTypeUtils = {
  notConfiguredLabel,
  getLimitAsOfToday,
  getLimitsInEffectiveDateOrder,
  getLimitFromRow,
  getPercentageLabel,
  getNumberOfIncreasesLabel,
  getIncreasesAllowedLabel
};

export default rentControlTypeUtils;
